import React, { useState, useEffect } from 'react';
import { 
  X, 
  Smartphone, 
  Monitor, 
  Share, 
  PlusSquare, 
  Download, 
  CheckCircle2, 
  ArrowRight,
  ShieldCheck,
  Zap,
  WifiOff,
  ExternalLink
} from 'lucide-react';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface PWAInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  isIOS: boolean;
  isAndroid: boolean;
  isDesktop: boolean;
  isInstallable: boolean;
  onDirectInstall?: () => Promise<boolean>;
}

export const PWAInstallModal: React.FC<PWAInstallModalProps> = ({
  isOpen,
  onClose,
  isIOS,
  isAndroid,
  isDesktop,
  isInstallable,
  onDirectInstall
}) => {
  const tokens = useThemeTokens();
  
  // Default tab based on user's active platform
  const [activeTab, setActiveTab] = useState<'ios' | 'android' | 'desktop'>(() => {
    if (isIOS) return 'ios';
    if (isAndroid) return 'android';
    return 'desktop';
  });

  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isIOS) setActiveTab('ios');
      else if (isAndroid) setActiveTab('android');
      else setActiveTab('desktop');
    }
  }, [isOpen, isIOS, isAndroid]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleDirectInstallClick = async () => {
    if (!onDirectInstall) return;
    setIsInstalling(true);
    try {
      const accepted = await onDirectInstall();
      if (accepted) {
        onClose();
      }
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[999] bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 overflow-y-auto custom-scrollbar animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="pwa-modal-title"
    >
      <div 
        className="bg-white dark:bg-slate-900 rounded-3xl sm:rounded-[2.5rem] p-5 sm:p-8 max-w-lg w-full relative shadow-2xl border border-slate-200 dark:border-slate-800 space-y-6 my-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-5 right-5 sm:top-6 sm:right-6 p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer"
          aria-label="Close installation guide"
        >
          <X size={20} />
        </button>

        {/* App Identity Banner */}
        <div className="flex items-center gap-4 pr-8">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden shadow-lg border border-slate-200/80 dark:border-slate-700/80 bg-[#07182b] shrink-0 flex items-center justify-center p-1 relative group">
            <img 
              src="/icons/pwa-192x192.png" 
              alt="Diamond School Of Nursing Study Hub Icon" 
              className="w-full h-full object-contain rounded-xl"
              onError={(e) => {
                // Fallback to SVG
                (e.target as HTMLImageElement).src = '/icons/icon.svg';
              }}
            />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider ${tokens.badge}`}>
                <ShieldCheck size={11} className="shrink-0" />
                Verified PWA
              </span>
            </div>
            <h3 id="pwa-modal-title" className={`text-lg sm:text-xl font-black text-slate-900 dark:text-white mt-1 leading-tight ${tokens.headerFont}`}>
              Diamond Study Hub App
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-semibold mt-0.5">
              Install native-feel app on your device
            </p>
          </div>
        </div>

        {/* Direct Install CTA when Chromium install prompt is ready */}
        {isInstallable && (activeTab === 'android' || activeTab === 'desktop') && (
          <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-emerald-500/10 border border-emerald-500/20 dark:border-emerald-500/30 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 text-emerald-800 dark:text-emerald-300">
              <Download size={18} className="shrink-0 text-emerald-600 dark:text-emerald-400" />
              <span className="text-xs font-black tracking-tight">1-Click Fast Install Ready</span>
            </div>
            <button
              onClick={handleDirectInstallClick}
              disabled={isInstalling}
              className={`w-full sm:w-auto px-5 py-2.5 rounded-xl font-black text-xs text-white shadow-md active:scale-95 transition-all flex items-center justify-center gap-2 ${tokens.primaryBtn}`}
            >
              <Download size={14} />
              {isInstalling ? 'Installing...' : 'Install Now'}
            </button>
          </div>
        )}

        {/* Device Platform Tabs */}
        <div className="flex p-1 bg-slate-100 dark:bg-slate-800/80 rounded-2xl gap-1">
          <button
            onClick={() => setActiveTab('ios')}
            className={`flex-1 py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'ios'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Smartphone size={14} />
            <span>iPhone / iPad</span>
          </button>
          <button
            onClick={() => setActiveTab('android')}
            className={`flex-1 py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'android'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Smartphone size={14} />
            <span>Android</span>
          </button>
          <button
            onClick={() => setActiveTab('desktop')}
            className={`flex-1 py-2 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
              activeTab === 'desktop'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
            }`}
          >
            <Monitor size={14} />
            <span>Desktop</span>
          </button>
        </div>

        {/* Tab Content: iOS Safari Instructions */}
        {activeTab === 'ios' && (
          <div className="space-y-3.5 animate-fade-in text-slate-700 dark:text-slate-200">
            <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/60 rounded-xl text-[11px] font-semibold text-amber-900 dark:text-amber-300">
              Note: Apple Safari requires installing via the Share menu below.
            </div>
            
            <div className="space-y-2.5">
              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  1
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  Open this page in <strong className="text-slate-900 dark:text-white">Safari</strong> and tap the{' '}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-sky-600 dark:text-sky-400 font-black mx-1 shadow-2xs">
                    <Share size={12} /> Share
                  </span>{' '}
                  button in Safari's bottom toolbar.
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  2
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  Scroll down the share sheet and tap{' '}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white font-black mx-1 shadow-2xs">
                    <PlusSquare size={12} className="text-indigo-600 dark:text-indigo-400" /> Add to Home Screen
                  </span>.
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  3
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  Tap <strong className="text-slate-900 dark:text-white">Add</strong> in the top-right corner. The official Diamond School app icon is placed on your home screen!
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content: Android Instructions */}
        {activeTab === 'android' && (
          <div className="space-y-3.5 animate-fade-in text-slate-700 dark:text-slate-200">
            <div className="space-y-2.5">
              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  1
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  In <strong className="text-slate-900 dark:text-white">Chrome</strong> or <strong className="text-slate-900 dark:text-white">Samsung Internet</strong>, tap the browser menu{' '}
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-800 dark:text-slate-100 font-black mx-1 shadow-2xs">
                    ⋮
                  </span>{' '}
                  (three dots in upper right).
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  2
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  Select <span className="font-black text-slate-900 dark:text-white">"Install app"</span> or <span className="font-black text-slate-900 dark:text-white">"Add to Home screen"</span>.
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  3
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  Confirm <strong className="text-slate-900 dark:text-white">Install</strong>. The full native WebAPK app with full-bleed diamond logo will be added to your app drawer.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Content: Desktop Instructions */}
        {activeTab === 'desktop' && (
          <div className="space-y-3.5 animate-fade-in text-slate-700 dark:text-slate-200">
            <div className="space-y-2.5">
              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  1
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  In <strong className="text-slate-900 dark:text-white">Chrome</strong> or <strong className="text-slate-900 dark:text-white">Edge</strong>, look at the right side of the address bar for the{' '}
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white font-black mx-1 shadow-2xs">
                    <Download size={11} className="text-indigo-600 dark:text-indigo-400" /> Install
                  </span> icon.
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  2
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  Or click <span className="font-black text-slate-900 dark:text-white">Menu (⋮)</span> →{' '}
                  <span className="font-black text-slate-900 dark:text-white">Save and share</span> →{' '}
                  <span className="font-black text-slate-900 dark:text-white">Install Study Hub</span>.
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800">
                <div className="w-7 h-7 rounded-xl bg-[#07182b] text-white flex items-center justify-center text-xs font-black shrink-0 shadow-sm">
                  3
                </div>
                <div className="text-xs font-bold leading-relaxed pt-0.5">
                  Launch Study Hub from your Windows Start Menu, Taskbar, or macOS Dock as a dedicated standalone window!
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Benefits Badges */}
        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 text-center space-y-1">
            <Zap size={14} className="mx-auto text-amber-500" />
            <p className="text-[10px] font-black text-slate-800 dark:text-slate-200 leading-tight">Instant Launch</p>
            <p className="text-[8px] text-slate-400 font-medium">No browser tabs</p>
          </div>
          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 text-center space-y-1">
            <CheckCircle2 size={14} className="mx-auto text-emerald-500" />
            <p className="text-[10px] font-black text-slate-800 dark:text-slate-200 leading-tight">100% Focused</p>
            <p className="text-[8px] text-slate-400 font-medium">Clean fullscreen</p>
          </div>
          <div className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800/40 text-center space-y-1">
            <WifiOff size={14} className="mx-auto text-sky-500" />
            <p className="text-[10px] font-black text-slate-800 dark:text-slate-200 leading-tight">Offline Cached</p>
            <p className="text-[8px] text-slate-400 font-medium">Core guides ready</p>
          </div>
        </div>

        {/* Dismiss Button */}
        <button
          onClick={onClose}
          className="w-full py-3.5 bg-slate-900 dark:bg-slate-800 hover:bg-slate-800 dark:hover:bg-slate-700 text-white rounded-2xl font-black text-xs tracking-wider transition-all cursor-pointer shadow-md active:scale-95"
        >
          Got It
        </button>
      </div>
    </div>
  );
};
